import type { Decision, Lifecycle, Confidence } from '@mas-flow/types';
import type { FilterState } from '../lib/filters/types';
import { EMPTY_FILTERS } from '../lib/filters/types';
import { useCategories } from '../hooks/useCategories';

const DECISION_OPTIONS: (Decision | null)[] = [null, 'Now', 'Next', 'Later', 'Parked'];
const LIFECYCLE_OPTIONS: Lifecycle[] = ['Submitted', 'InTriage', 'Scored', 'Decided', 'Done'];
const CONFIDENCE_OPTIONS: Confidence[] = ['High', 'Medium', 'Low'];

export type FilterToggleId = 'anchorsOnly' | 'showArchived';

export interface FilterToolbarProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  hiddenToggles?: readonly FilterToggleId[];
}

export function FilterToolbar({ filters, onChange, hiddenToggles = [] }: FilterToolbarProps) {
  const { data: categories } = useCategories();
  const categoryOptions = (categories ?? []).filter((c) => !c.archived || filters.category.includes(c.id));

  const toggle = <T,>(arr: readonly T[], value: T): T[] =>
    arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];

  const clearAll = () => onChange(EMPTY_FILTERS);
  const hasAnyFilter =
    filters.q !== '' ||
    filters.category.length > 0 ||
    filters.decision.length > 0 ||
    filters.lifecycle.length > 0 ||
    filters.confidence.length > 0 ||
    filters.anchorsOnly ||
    filters.showArchived;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-white p-2 text-sm">
      <input
        type="search"
        value={filters.q}
        onChange={(e) => onChange({ ...filters, q: e.target.value })}
        placeholder="Search title / problem / outcome"
        className="rounded border border-slate-300 px-2 py-1 text-sm"
      />
      <Group label="Category">
        {categoryOptions.map((c) => (
          <Chip key={c.id} active={filters.category.includes(c.id)} onClick={() => onChange({ ...filters, category: toggle(filters.category, c.id) })}>
            {c.name}
          </Chip>
        ))}
      </Group>
      <Group label="Decision">
        {DECISION_OPTIONS.map((d) => (
          <Chip
            key={d ?? 'inbox'}
            active={filters.decision.includes(d)}
            onClick={() => onChange({ ...filters, decision: toggle(filters.decision, d) })}
          >
            {d ?? 'Inbox'}
          </Chip>
        ))}
      </Group>
      <Group label="Lifecycle">
        {LIFECYCLE_OPTIONS.map((l) => (
          <Chip key={l} active={filters.lifecycle.includes(l)} onClick={() => onChange({ ...filters, lifecycle: toggle(filters.lifecycle, l) })}>
            {l}
          </Chip>
        ))}
      </Group>
      <Group label="Confidence">
        {CONFIDENCE_OPTIONS.map((c) => (
          <Chip key={c} active={filters.confidence.includes(c)} onClick={() => onChange({ ...filters, confidence: toggle(filters.confidence, c) })}>
            {c}
          </Chip>
        ))}
      </Group>
      {!hiddenToggles.includes('anchorsOnly') && (
        <Toggle active={filters.anchorsOnly} onClick={() => onChange({ ...filters, anchorsOnly: !filters.anchorsOnly })}>
          ⚓ Anchors only
        </Toggle>
      )}
      {!hiddenToggles.includes('showArchived') && (
        <Toggle active={filters.showArchived} onClick={() => onChange({ ...filters, showArchived: !filters.showArchived })}>
          Show archived
        </Toggle>
      )}
      {hasAnyFilter && (
        <button type="button" onClick={clearAll} className="ml-auto text-xs text-slate-600 underline">
          Clear filters
        </button>
      )}
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs font-medium text-slate-500">{label}:</span>
      {children}
    </div>
  );
}
function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2 py-0.5 text-xs ${active ? 'border-brand bg-brand text-white' : 'border-slate-300 bg-white text-slate-700'}`}
    >
      {children}
    </button>
  );
}
function Toggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded border px-2 py-1 text-xs ${active ? 'border-brand bg-brand-wash text-brand-dark' : 'border-slate-300 bg-white text-slate-700'}`}
    >
      {children}
    </button>
  );
}
